import { useState, useRef, useCallback, useEffect } from 'react'
import { exclusionsService } from '@/services/exclusions-service'
import type { ExclusionItem } from '@/types'

/* ── Inline SVG icons ── */
const DeleteIcon = () => (
  <svg style={{ width: 13, height: 13 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

// const _EditIcon = () => (
//   <svg style={{ width: 13, height: 13 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
//     <path d="m15 5 4 4" />
//   </svg>
// )

const CheckIcon = () => (
  <svg style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const DragIcon = () => (
  <svg style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="6" r="1" fill="currentColor" /><circle cx="15" cy="6" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="18" r="1" fill="currentColor" /><circle cx="15" cy="18" r="1" fill="currentColor" />
  </svg>
)

export function ExclusionsPage() {
  const [exclusions, setExclusions] = useState<ExclusionItem[]>([])
  const [inclusions, setInclusions] = useState<ExclusionItem[]>([])
  const [search, setSearch] = useState('')
  const [newValue, setNewValue] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [activeTab, setActiveTab] = useState<'exclusions' | 'inclusions'>('inclusions')
  const [loading, setLoading] = useState(true)
  const addInputRef = useRef<HTMLInputElement>(null)

  // Fetch both lists on mount
  useEffect(() => {
    let cancelled = false
    const fetchData = async () => {
      try {
        setLoading(true)
        const [excl, incl] = await Promise.all([
          exclusionsService.listByType('exclusion'),
          exclusionsService.listByType('inclusion'),
        ])
        if (!cancelled) {
          setExclusions(excl)
          setInclusions(incl)
        }
      } catch (err) {
        console.error('Failed to load exclusions:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchData()
    return () => { cancelled = true }
  }, [])

  const currentItems = activeTab === 'exclusions' ? exclusions : inclusions
  const setCurrentItems = activeTab === 'exclusions' ? setExclusions : setInclusions
  const currentType = activeTab === 'exclusions' ? 'exclusion' as const : 'inclusion' as const
  const isSearching = search.trim().length > 0

  // Filter items by search
  const filteredItems = isSearching
    ? currentItems.filter((item) => item.text.toLowerCase().includes(search.toLowerCase()))
    : currentItems

  const handleAdd = async () => {
    if (!newValue.trim()) return
    try {
      const created = await exclusionsService.create(currentType, newValue.trim())
      // Prepend to top
      setCurrentItems((prev) => [created, ...prev])
      setNewValue('')
      addInputRef.current?.focus()
    } catch (err) {
      console.error('Failed to add item:', err)
    }
  }

  const handleDelete = async (id: string) => {
    const prev = currentItems
    setCurrentItems((items) => items.filter((item) => item.id !== id))
    try {
      await exclusionsService.remove(id)
    } catch (err) {
      console.error('Failed to delete item:', err)
      setCurrentItems(prev) // rollback
    }
  }

  const handleStartEdit = (id: string, text: string) => {
    setEditingId(id)
    setEditValue(text)
  }

  const handleSaveEdit = async (id: string) => {
    if (!editValue.trim()) return
    try {
      const updated = await exclusionsService.update(id, editValue.trim())
      setCurrentItems((items) =>
        items.map((item) => (item.id === id ? updated : item))
      )
    } catch (err) {
      console.error('Failed to update item:', err)
    }
    setEditingId(null)
    setEditValue('')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditValue('')
  }

  /* ── Drag-to-reorder (ghost pattern from writeup panel) ── */
  const handleDragStart = useCallback((e: React.MouseEvent, rowId: string) => {
    e.preventDefault()
    const rowEl = (e.target as HTMLElement).closest('tr[data-row-id]') as HTMLElement
    if (!rowEl) return

    const rect = rowEl.getBoundingClientRect()
    const tableEl = rowEl.closest('table') as HTMLElement

    // Create floating clone
    const clone = document.createElement('div')
    clone.style.position = 'fixed'
    clone.style.left = rect.left + 'px'
    clone.style.top = rect.top + 'px'
    clone.style.width = rect.width + 'px'
    clone.style.height = rect.height + 'px'
    clone.style.background = '#fff'
    clone.style.boxShadow = '0 8px 24px rgba(0,0,0,0.15), 0 2px 6px rgba(0,0,0,0.1)'
    clone.style.borderRadius = '6px'
    clone.style.border = '1px solid var(--s200)'
    clone.style.zIndex = '100'
    clone.style.pointerEvents = 'none'
    clone.style.display = 'flex'
    clone.style.alignItems = 'center'
    clone.style.padding = '0 16px'
    clone.style.fontSize = '14px'
    clone.style.fontWeight = '500'
    clone.style.color = 'var(--s900)'

    const textCell = rowEl.querySelector('td:nth-child(3)') as HTMLElement
    clone.textContent = textCell?.textContent || ''
    document.body.appendChild(clone)

    rowEl.style.opacity = '0.25'

    const startY = e.clientY
    let lastSwapId: string | null = null

    const handleMouseMove = (me: MouseEvent) => {
      const dy = me.clientY - startY
      clone.style.top = (rect.top + dy) + 'px'

      if (!tableEl) return
      const rows = Array.from(tableEl.querySelectorAll('tr[data-row-id]')).filter(
        (r) => r !== rowEl
      )

      for (const r of rows) {
        const rr = r.getBoundingClientRect()
        const mid = rr.top + rr.height / 2
        const targetId = r.getAttribute('data-row-id')
        if (!targetId || targetId === rowId) continue

        if (me.clientY >= rr.top && me.clientY <= rr.bottom) {
          const ghostRect = rowEl.getBoundingClientRect()
          const dragIsAbove = ghostRect.top < rr.top

          if ((dragIsAbove && me.clientY > mid) || (!dragIsAbove && me.clientY < mid)) {
            if (lastSwapId !== targetId) {
              lastSwapId = targetId
              reorderItems(rowId, targetId, dragIsAbove)
            }
          }
          break
        }
      }
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      clone.remove()
      rowEl.style.opacity = ''

      // Persist reorder to API
      const items = activeTab === 'exclusions' ? exclusions : inclusions
      const ids = items.map((i) => i.id)
      exclusionsService.reorder(ids).catch((err) => {
        console.error('Failed to persist reorder:', err)
      })
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }, [activeTab, exclusions, inclusions])

  const reorderItems = (dragId: string, targetId: string, insertAfter: boolean) => {
    setCurrentItems((prev) => {
      const newItems = [...prev]
      const dragIdx = newItems.findIndex((i) => i.id === dragId)
      const targetIdx = newItems.findIndex((i) => i.id === targetId)
      if (dragIdx === -1 || targetIdx === -1) return prev
      const [dragged] = newItems.splice(dragIdx, 1)
      const newTargetIdx = newItems.findIndex((i) => i.id === targetId)
      newItems.splice(insertAfter ? newTargetIdx + 1 : newTargetIdx, 0, dragged)
      return newItems
    })
  }

  return (
    <div>
      {/* Page Header — title only, no add button */}
      <div className="est-list-head">
        <h1 className="est-list-title">Inclusions & Exclusions</h1>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="search-icon">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="est-list-tabs">
        <button
          className={`est-list-tab ${activeTab === 'inclusions' ? 'active' : ''}`}
          onClick={() => { setActiveTab('inclusions'); setEditingId(null) }}
        >
          Inclusions ({inclusions.length})
        </button>
        <button
          className={`est-list-tab ${activeTab === 'exclusions' ? 'active' : ''}`}
          onClick={() => { setActiveTab('exclusions'); setEditingId(null) }}
        >
          Exclusions ({exclusions.length})
        </button>
      </div>

      {/* Table in card */}
      <div className="card est-list-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--s400)' }}>
            Loading...
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th style={{ width: 40 }}>#</th>
                <th>{activeTab === 'exclusions' ? 'Exclusion' : 'Inclusion'}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {/* ── Inline add row — always visible at top ── */}
              <tr>
                <td style={{ width: 28 }}></td>
                <td style={{ width: 40 }}>
                  <button
                    onClick={handleAdd}
                    title="Add item"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: '50%', background: 'var(--blue)', color: '#fff',
                      fontSize: 15, fontWeight: 700, lineHeight: 1, padding: '0 0 1px 0',
                      border: 'none', cursor: 'pointer', transition: 'opacity 0.15s',
                      opacity: newValue.trim() ? 1 : 0.5,
                    }}
                  ><span style={{ marginTop: '-1.5px' }}>+</span></button>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      ref={addInputRef}
                      className="search-input"
                      style={{ maxWidth: 'none', flex: 1, padding: '6px 10px', fontSize: 14, border: '1px dashed var(--s200)' }}
                      placeholder={`Type a new ${currentType} and press Enter...`}
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAdd()
                      }}
                    />
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {newValue.trim() && (
                    <button
                      className="tbl-action-btn"
                      style={{ color: 'var(--teal)' }}
                      title="Add item"
                      onClick={handleAdd}
                    >
                      <CheckIcon />
                    </button>
                  )}
                </td>
              </tr>

              {filteredItems.length === 0 && !isSearching ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--s400)' }}>
                    No {activeTab} added yet — type above to add your first item
                  </td>
                </tr>
              ) : filteredItems.length === 0 && isSearching ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--s400)' }}>
                    No matching items found
                  </td>
                </tr>
              ) : (
                filteredItems.map((item, idx) => (
                  <tr key={item.id} data-row-id={item.id}>
                    {/* Drag handle — hidden when searching */}
                    <td style={{ width: 28, padding: '0 4px' }}>
                      {!isSearching && (
                        <span
                          style={{ cursor: 'grab', color: 'var(--s300)', display: 'inline-flex', alignItems: 'center' }}
                          onMouseDown={(e) => handleDragStart(e, item.id)}
                        >
                          <DragIcon />
                        </span>
                      )}
                    </td>
                    <td style={{ width: 40, color: 'var(--s400)' }}>{idx + 1}</td>
                    <td>
                      {editingId === item.id ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input
                            className="search-input"
                            style={{ maxWidth: 'none', flex: 1, padding: '6px 10px', fontSize: 14 }}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(item.id)
                              else if (e.key === 'Escape') handleCancelEdit()
                            }}
                          />
                          <button
                            className="tbl-action-btn"
                            style={{ color: 'var(--teal)' }}
                            title="Save"
                            onClick={() => handleSaveEdit(item.id)}
                          >
                            <CheckIcon />
                          </button>
                          <button
                            className="tbl-action-btn"
                            style={{ color: 'var(--s400)' }}
                            title="Cancel"
                            onClick={handleCancelEdit}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      ) : (
                        <span
                          style={{ fontWeight: 500, color: 'var(--s900)' }}
                          onDoubleClick={() => handleStartEdit(item.id, item.text)}
                          title="Double-click to edit"
                        >
                          {item.text}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {editingId !== item.id && (
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                          <button
                            className="tbl-action-btn tbl-action-btn--danger"
                            title="Remove"
                            onClick={() => handleDelete(item.id)}
                          >
                            <DeleteIcon />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
